import React, { useState, useEffect } from 'react';
import './CreateItem.css';

interface UploadXLSProps {
  onUploadComplete?: () => void;
}

interface ColumnMapping {
  batchNumber: string;
  productNumber: string;
  greenCard?: string;
}

interface ProcessingResult {
  row: number;
  invoice_id?: string;
  invoice_number?: string;
  status: string;
  error?: string;
}

const UploadXLS: React.FC<UploadXLSProps> = ({ onUploadComplete }) => {
  const [xlsFile, setXlsFile] = useState<File | null>(null);
  const [xlsData, setXlsData] = useState<string[][]>([]);
  const [xlsHeaders, setXlsHeaders] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    batchNumber: '',
    productNumber: '',
    greenCard: ''
  });
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [warehouses, setWarehouses] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadedToS3, setUploadedToS3] = useState(false);
  const [showInvoiceCreation, setShowInvoiceCreation] = useState(false);
  const [creatingInvoices, setCreatingInvoices] = useState(false);
  const [invoiceProgress, setInvoiceProgress] = useState(0);
  const [results, setResults] = useState<ProcessingResult[]>([]);

  
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        
        
        const mockWarehouses = ['Склад 1', 'Склад 2', 'Склад 3']; 
        setWarehouses(mockWarehouses);
        if (mockWarehouses.length > 0) {
          setSelectedWarehouse(mockWarehouses[0]);
        }
      } catch (err) {
        console.error('Грешка при зареждането на складовете:', err);
      }
    };

    fetchWarehouses();
  }, []);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExtension = file.name.toLowerCase();
    if (!fileExtension.endsWith('.xls') && !fileExtension.endsWith('.xlsx')) {
      setError('Моля, качете Excel файл (.xls или .xlsx)');
      return;
    }

    setXlsFile(file);
    setError('');
    setSuccess('');
    setResults([]);
    setUploadedToS3(false);
    setShowInvoiceCreation(false);
    setUploadProgress(0);

    
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        setError('Excel файлът изглежда празен');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const data = lines.slice(1).map(line => 
        line.split(',').map(cell => cell.trim().replace(/"/g, ''))
      );

      setXlsHeaders(headers);
      setXlsData(data);
      
      
      setColumnMapping({
        batchNumber: '',
        productNumber: '',
        greenCard: ''
      });
    };

    reader.readAsText(file);
  };

  const handleColumnMappingChange = (field: keyof ColumnMapping, value: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const simulateProgress = (setProgressFunction: (progress: number) => void, duration: number = 3000) => {
    let progress = 0;
    const increment = 100 / (duration / 100);
    
    const interval = setInterval(() => {
      progress += increment;
      if (progress >= 100) {
        setProgressFunction(100);
        clearInterval(interval);
      } else {
        setProgressFunction(Math.min(progress, 100));
      }
    }, 100);
    
    return interval;
  };

  const handleUploadToS3 = async () => {
    if (!xlsFile || !selectedWarehouse) {
      setError('Моля, изберете файл и склад');
      return;
    }

    setUploading(true);
    setError('');
    setUploadProgress(0);

    
    const progressInterval = simulateProgress(setUploadProgress, 2000);

    try {
      const formData = new FormData();
      formData.append('xlsFile', xlsFile);
      formData.append('year', selectedYear.toString());
      formData.append('month', selectedMonth.toString());
      formData.append('warehouse', selectedWarehouse);

      
      await new Promise(resolve => setTimeout(resolve, 2000));

      
      
      
      
      

      setSuccess(`Файлът е качен успешно! Локация: ${selectedYear}/${selectedMonth.toString().padStart(2, '0')}/${selectedWarehouse}/`);
      setUploadedToS3(true);
      setShowInvoiceCreation(true);
      
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (err: any) {
      console.error('Грешка при качването в S3:', err);
      setError(err.response?.data?.message || 'Неуспешно качване на файла в S3');
      clearInterval(progressInterval);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleCreateInvoices = async () => {
    if (!xlsData.length) {
      setError('Няма налични данни за създаване на фактури');
      return;
    }

    if (!columnMapping.batchNumber || !columnMapping.productNumber) {
      setError('Моля, задайте необходимите колони: Номер на партида и Номер на продукт');
      return;
    }

    setCreatingInvoices(true);
    setError('');
    setInvoiceProgress(0);

    
    const progressInterval = simulateProgress(setInvoiceProgress, 3000);

    try {
      
      await new Promise(resolve => setTimeout(resolve, 3000));

      
      
      
      

      
      const mockResponse = { successful: 5, failed: 1, results: [] };

      setSuccess(`Създадени са ${mockResponse.successful} фактури успешно, ${mockResponse.failed} неуспешни.`);
      setResults(mockResponse.results || []);
      
    } catch (err: any) {
      console.error('Грешка при създаването на фактури:', err);
      setError(err.response?.data?.message || 'Неуспешно създаване на фактури');
      clearInterval(progressInterval);
      setInvoiceProgress(0);
    } finally {
      setCreatingInvoices(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  const months = [
    { value: 1, name: 'Януари' }, { value: 2, name: 'Февруари' }, { value: 3, name: 'Март' },
    { value: 4, name: 'Април' }, { value: 5, name: 'Май' }, { value: 6, name: 'Юни' },
    { value: 7, name: 'Юли' }, { value: 8, name: 'Август' }, { value: 9, name: 'Септември' },
    { value: 10, name: 'Октомври' }, { value: 11, name: 'Ноември' }, { value: 12, name: 'Декември' }
  ];

  return (
    <div className="create-item">
      <div className="create-item-card">
        <h2>Качване на Excel Файлове</h2>
        <p className="subtitle">Качете Excel файлове в S3 със структура година/месец/склад</p>
        
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        <div className="upload-form">
          {}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="warehouse">Склад *</label>
              <select
                id="warehouse"
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
                disabled={uploading || creatingInvoices}
                required
              >
                <option value="">Изберете склад...</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse} value={warehouse}>
                    {warehouse}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="year">Година *</label>
              <select
                id="year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                disabled={uploading || creatingInvoices}
                required
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="month">Месец *</label>
              <select
                id="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                disabled={uploading || creatingInvoices}
                required
              >
                {months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {}
          <div className="form-group">
            <label htmlFor="xlsFile">Качете Excel Файл *</label>
            <input
              type="file"
              id="xlsFile"
              accept=".xls,.xlsx"
              onChange={handleFileUpload}
              disabled={uploading || creatingInvoices}
              className="file-input"
            />
            {xlsFile && (
              <div className="file-info">
                <span>📄 {xlsFile.name}</span>
                <span className="file-size">({xlsData.length} реда)</span>
                {uploadedToS3 && <span className="uploaded-badge">✅ Качен в S3</span>}
              </div>
            )}
          </div>

          {}
          {uploading && (
            <div className="progress-section">
              <div className="progress-header">
                <span>Качване в S3...</span>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {}
          {xlsFile && selectedWarehouse && !uploadedToS3 && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleUploadToS3}
                className="upload-button"
                disabled={uploading}
              >
                {uploading ? 'Качване в S3...' : 'Качи в S3'}
              </button>
            </div>
          )}

          {}
          {showInvoiceCreation && uploadedToS3 && (
            <div className="invoice-creation-section">
              <h3>По избор: Създайте Фактури от Данните</h3>
              <p className="subtitle">Свържете колони за създаване на фактури в Zoho (незадължителна стъпка)</p>

              {}
              {xlsHeaders.length > 0 && (
                <div className="column-mapping">
                  <h4>Свържете Excel Колони</h4>
                  
                  <div className="form-group">
                    <label htmlFor="batchNumber">Колона за Номер на Партида *</label>
                    <select
                      id="batchNumber"
                      value={columnMapping.batchNumber}
                      onChange={(e) => handleColumnMappingChange('batchNumber', e.target.value)}
                      disabled={creatingInvoices}
                      required
                    >
                      <option value="">Изберете колона...</option>
                      {xlsHeaders.map((header, index) => (
                        <option key={index} value={index.toString()}>
                          {header} (Колона {index + 1})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="productNumber">Колона за Номер на Продукт *</label>
                    <select
                      id="productNumber"
                      value={columnMapping.productNumber}
                      onChange={(e) => handleColumnMappingChange('productNumber', e.target.value)}
                      disabled={creatingInvoices}
                      required
                    >
                      <option value="">Изберете колона...</option>
                      {xlsHeaders.map((header, index) => (
                        <option key={index} value={index.toString()}>
                          {header} (Колона {index + 1})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="greenCard">Колона за Зелена Карта (По избор)</label>
                    <select
                      id="greenCard"
                      value={columnMapping.greenCard}
                      onChange={(e) => handleColumnMappingChange('greenCard', e.target.value)}
                      disabled={creatingInvoices}
                    >
                      <option value="">Изберете колона...</option>
                      {xlsHeaders.map((header, index) => (
                        <option key={index} value={index.toString()}>
                          {header} (Колона {index + 1})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {xlsData.length > 0 && columnMapping.batchNumber && columnMapping.productNumber && (
                <div className="preview-section">
                  <h4>Преглед (Първи 3 реда)</h4>
                  <div className="preview-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Номер на Партида</th>
                          <th>Номер на Продукт</th>
                          <th>Зелена Карта</th>
                        </tr>
                      </thead>
                      <tbody>
                        {xlsData.slice(0, 3).map((row, index) => (
                          <tr key={index}>
                            <td>{row[parseInt(columnMapping.batchNumber)] || '-'}</td>
                            <td>{row[parseInt(columnMapping.productNumber)] || '-'}</td>
                            <td>{columnMapping.greenCard ? (row[parseInt(columnMapping.greenCard)] || '-') : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {}
              {creatingInvoices && (
                <div className="progress-section">
                  <div className="progress-header">
                    <span>Създаване на фактури...</span>
                    <span>{Math.round(invoiceProgress)}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${invoiceProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCreateInvoices}
                  className="submit-button"
                  disabled={creatingInvoices || !columnMapping.batchNumber || !columnMapping.productNumber}
                >
                  {creatingInvoices ? 'Създаване на фактури...' : `Създай ${xlsData.length} Фактури`}
                </button>
              </div>
            </div>
          )}

          {}
          {results.length > 0 && (
            <div className="results-section">
              <h3>Резултати от Създаването на Фактури</h3>
              <div className="results-table">
                <table>
                  <thead>
                    <tr>
                      <th>Ред</th>
                      <th>Статус</th>
                      <th>Номер на Фактура</th>
                      <th>Детайли</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result, index) => (
                      <tr key={index} className={result.status === 'success' ? 'success-row' : 'error-row'}>
                        <td>{result.row}</td>
                        <td>
                          <span className={`status-badge ${result.status}`}>
                            {result.status === 'success' ? '✅' : '❌'} {result.status === 'success' ? 'Успех' : 'Грешка'}
                          </span>
                        </td>
                        <td>{result.invoice_number || '-'}</td>
                        <td>{result.error || result.invoice_id || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadXLS; 